import { useState, useEffect, useCallback } from 'react';
import { resolveToken, getDebtSummary } from '../services/api';

/**
 * Extracts the token from the current URL.
 * Supports both path-based (/{token}) and query-based (?token={token}) formats.
 * @returns {string|null} The token or null if not found
 */
export function extractTokenFromUrl() {
  // Try path-based: /a7Bx3k → token is the first path segment after root
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  if (pathSegments.length === 1 && pathSegments[0].length >= 4 && pathSegments[0].length <= 12) {
    return pathSegments[0];
  }

  // Try query parameter: ?token=a7Bx3k
  const params = new URLSearchParams(window.location.search);
  const queryToken = params.get('token');
  if (queryToken) {
    return queryToken;
  }

  // Try hash-based: #/a7Bx3k
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (hash && hash.length >= 4 && hash.length <= 12 && !hash.includes('/')) {
    return hash;
  }

  return null;
}

/**
 * States for the token resolution flow:
 * - extracting: reading token from URL
 * - resolving: calling backend to validate token
 * - loading: fetching client data after token validated
 * - ready: data loaded, portal ready
 * - error: something went wrong
 * - no-token: no token found in URL
 */

/**
 * Custom hook that manages the full token → data loading flow.
 * 1. Extracts token from URL
 * 2. Resolves token to LoanApplicationId via backend
 * 3. Loads client debt summary
 */
export default function useTokenResolver() {
  const [state, setState] = useState({
    status: 'extracting', // extracting | resolving | loading | ready | error | no-token
    token: null,
    loanApplicationId: null,
    clientData: null,
    error: null,
  });

  const loadClientData = useCallback(async (token) => {
    setState(prev => ({ ...prev, status: 'resolving', token }));

    try {
      // Step 1: Resolve token to LoanApplicationId
      const resolved = await resolveToken(token);

      if (!resolved.isValid) {
        setState(prev => ({
          ...prev,
          status: 'error',
          error: {
            type: 'invalid-token',
            message: 'This link is not valid. Please check the link in your SMS and try again.',
          },
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        status: 'loading',
        loanApplicationId: resolved.loanApplicationId,
      }));

      // Step 2: Load debt summary / client data
      const summary = await getDebtSummary(token);

      setState(prev => ({
        ...prev,
        status: 'ready',
        clientData: summary,
      }));
    } catch (err) {
      let errorInfo;

      if (err.status === 404) {
        errorInfo = {
          type: 'invalid-token',
          message: 'This link is not valid. Please check the link in your SMS and try again.',
        };
      } else if (err.status === 401) {
        errorInfo = {
          type: 'unauthorized',
          message: 'Access denied. Please use the link sent to your phone.',
        };
      } else if (!navigator.onLine) {
        errorInfo = {
          type: 'network',
          message: 'You appear to be offline. Please check your internet connection and try again.',
        };
      } else {
        errorInfo = {
          type: 'server',
          message: 'Something went wrong. Please try again in a moment.',
        };
      }

      setState(prev => ({
        ...prev,
        status: 'error',
        error: errorInfo,
      }));
    }
  }, []);

  const retry = useCallback(() => {
    if (state.token) {
      loadClientData(state.token);
    }
  }, [state.token, loadClientData]);

  useEffect(() => {
    const token = extractTokenFromUrl();

    if (!token) {
      setState(prev => ({
        ...prev,
        status: 'no-token',
        error: {
          type: 'no-token',
          message: 'No account token found. Please use the link sent to your phone via SMS.',
        },
      }));
      return;
    }

    loadClientData(token);
  }, [loadClientData]);

  return {
    ...state,
    retry,
    isLoading: state.status === 'extracting' || state.status === 'resolving' || state.status === 'loading',
    isReady: state.status === 'ready',
    isError: state.status === 'error' || state.status === 'no-token',
  };
}
